import type { PrintTemplateItem } from "@repo/types";
import type { PrintFieldDef } from "@repo/print-pdf/arbre/print/template";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Checkbox } from "@repo/ui/components/ui/checkbox";
import { Input } from "@repo/ui/components/ui/input";
import { Eye, EyeOff, GripVertical, Lock, Settings2 } from "lucide-react";
import type { DraftDispatch } from "./use-template-draft";

interface Props {
  sectionId: string;
  columns: number;
  item: PrintTemplateItem;
  field: PrintFieldDef<any>;
  editing: boolean;
  onToggleEditing: () => void;
  dispatch: DraftDispatch;
}

/** Contenu d'une carte, partagé entre la carte déplaçable et son fantôme pendant le glisser-déposer. */
export function FieldCardBody({
  item,
  field,
  handle,
  actions,
}: {
  item: PrintTemplateItem;
  field: PrintFieldDef<any>;
  handle?: React.HTMLAttributes<HTMLButtonElement>;
  actions?: React.ReactNode;
}) {
  return (
    <div
      className={`group relative flex items-center gap-1 rounded-md border border-border bg-card px-1.5 py-1 text-xs shadow-sm ${item.visible ? "" : "opacity-60"}`}
    >
      <button
        type="button"
        aria-label={`Déplacer ${field.label}`}
        className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
        {...handle}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span
        className={`min-w-0 flex-1 break-words leading-tight ${item.visible ? "" : "line-through"}`}
        title={item.label || field.label}
      >
        {item.label || field.label}
      </span>
      {actions && (
        // Sur un champ visible, les icônes se superposent au libellé au survol ou au focus (elles ne lui prennent pas de
        // place : les libellés restent lisibles dans les colonnes étroites). Au toucher et sur un champ masqué, elles
        // restent affichées à la suite du libellé.
        <span
          className={
            item.visible
              ? "absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5 rounded bg-card opacity-0 shadow-sm transition-opacity focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:static [@media(hover:none)]:translate-y-0 [@media(hover:none)]:opacity-100 [@media(hover:none)]:shadow-none"
              : "flex shrink-0 items-center gap-0.5"
          }
        >
          {actions}
        </span>
      )}
    </div>
  );
}

export function FieldCard({
  sectionId,
  columns,
  item,
  field,
  editing,
  onToggleEditing,
  dispatch,
}: Props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.field });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? "opacity-40" : undefined}
    >
      <FieldCardBody
        item={item}
        field={field}
        handle={
          {
            ...attributes,
            ...listeners,
          } as React.HTMLAttributes<HTMLButtonElement>
        }
        actions={
          <>
            {field.locked ? (
              <Lock
                className="mx-1 h-3.5 w-3.5 text-muted-foreground"
                aria-label="Champ obligatoire"
              />
            ) : (
              <button
                type="button"
                onClick={() =>
                  dispatch({ type: "toggleItem", sectionId, field: item.field })
                }
                aria-label={`${item.visible ? "Masquer" : "Afficher"} ${field.label}`}
                aria-pressed={item.visible}
                className="rounded p-0.5 text-muted-foreground hover:text-foreground"
              >
                {item.visible ? (
                  <Eye className="h-3.5 w-3.5" />
                ) : (
                  <EyeOff className="h-3.5 w-3.5" />
                )}
              </button>
            )}
            {field.kind === "text" && (
              <button
                type="button"
                onClick={onToggleEditing}
                aria-label={`Réglages de ${field.label}`}
                aria-expanded={editing}
                className="rounded p-0.5 text-muted-foreground hover:text-foreground"
              >
                <Settings2 className="h-3.5 w-3.5" />
              </button>
            )}
          </>
        }
      />
      {editing && field.kind === "text" && (
        <div className="mt-1 space-y-1.5 rounded-md border border-dashed border-border bg-muted/40 p-2">
          <Input
            value={item.label ?? ""}
            placeholder={field.label}
            onChange={(e) =>
              dispatch({
                type: "setItemLabel",
                sectionId,
                field: item.field,
                label: e.target.value,
              })
            }
            className="h-7 text-xs"
            aria-label={`Libellé de ${field.label}`}
          />
          <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Checkbox
              checked={Boolean(item.hideIfEmpty)}
              onCheckedChange={() =>
                dispatch({
                  type: "toggleHideIfEmpty",
                  sectionId,
                  field: item.field,
                })
              }
              aria-label={`Masquer ${field.label} si vide`}
            />
            Masquer si la valeur est vide
          </label>
          <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            Colonne
            <select
              value={Math.min(item.col, columns)}
              onChange={(e) =>
                dispatch({
                  type: "setItemCol",
                  sectionId,
                  field: item.field,
                  col: Number(e.target.value),
                })
              }
              className="h-6 rounded-md border border-input bg-background px-1 text-xs"
              aria-label={`Colonne de ${field.label}`}
            >
              {Array.from({ length: columns }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  C{i + 1}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
    </div>
  );
}
